import { WORLD } from "./config.ts";
import { hash2 } from "./math.ts";
import type { Collider, Island } from "./world.ts";

export const SKULL_ORIGIN = { x: -1700, z: 1300 } as const;
const point = (x: number, y: number, z: number) => ({ x: SKULL_ORIGIN.x + x, y, z: SKULL_ORIGIN.z + z });
export type SkullFeet = { x: number; y: number; z: number };
const authoredPalms = [
  { x: -48, z: -82 }, { x: -45, z: -77 }, { x: -49, z: -72 }, { x: -15, z: -79 },
  { x: -8, z: -73 }, { x: -6, z: -68 }, { x: -42, z: -38 }, { x: -36, z: -35 },
  { x: -34, z: -30 }, { x: -7, z: -38 }, { x: 0, z: -34 }, { x: 4, z: -39 },
  { x: 21, z: -8 }, { x: 25, z: -4 }, { x: 19, z: 0 }, { x: 33, z: 22 },
  { x: 35, z: 28 }, { x: 38, z: 32 }, { x: -9, z: 22 }, { x: -15, z: 25 },
  { x: -14, z: 31 }, { x: -5, z: 51 }, { x: 0, z: 56 }, { x: 6, z: 53 },
  { x: -25, z: 45 }, { x: -28, z: 39 }, { x: 22, z: 61 }, { x: 27, z: 57 },
] as const;

export const SKULL_ISLE: Island = {
  id: "skull", name: "Skull Key", kind: "island", theme: "ruins",
  cx: SKULL_ORIGIN.x, cz: SKULL_ORIGIN.z, radius: 104, plateau: 1.8, peak: 34, seed: 97,
  harmonics: [{ k: 2, amp: .08, phase: .4 }, { k: 3, amp: .06, phase: 1.2 }], bumps: [],
  landmarks: [
    { id: "skull-pillars", kind: "pillars", label: "Salvage Gate", x: -30, z: -74 },
    { id: "skull-statue", kind: "statue", label: "Signal Mast", x: 8, z: 36 },
    { id: "skull-arch", kind: "arch", label: "Cliff Crossing", x: -25, z: -8 },
  ],
  digSpots: [
    { id: "skull-1", x: -20, z: -62, landmark: "skull-pillars", hint: "Inside the salvage gate, in the eastern corner of the lower ward." },
    { id: "skull-2", x: 14, z: 31, landmark: "skull-statue", hint: "South-east of the signal mast, beside the upper courtyard's canvas shelters." },
    { id: "skull-3", x: -25, z: 5, landmark: "skull-arch", hint: "Below the cliff crossing, on the sheltered return path north of the bridge." },
  ],
  // Rendering, chart canopies and collision all consume the retained authoritative set.
  get trees() { return SKULL_PALMS; },
};

type Terrace = { id: string; top: number; outline: readonly { x: number; z: number }[] };
const terraceFootprints: readonly Terrace[] = [
  { id: "lower", top: 8, outline: [{ x: -42, z: -91 }, { x: -15, z: -96 }, { x: 15, z: -78 }, { x: 34, z: -47 }, { x: 14, z: -27 }, { x: -38, z: -19 }, { x: -60, z: -36 }, { x: -60, z: -65 }] },
  { id: "middle", top: 20, outline: [{ x: -49, z: -45 }, { x: -22, z: -56 }, { x: 13, z: -42 }, { x: 47, z: -16 }, { x: 54, z: 30 }, { x: 27, z: 55 }, { x: -25, z: 42 }, { x: -56, z: 10 }] },
  { id: "upper", top: 34, outline: [{ x: -34, z: -2 }, { x: -17, z: -12 }, { x: 20, z: -8 }, { x: 47, z: 13 }, { x: 42, z: 53 }, { x: 17, z: 72 }, { x: -18, z: 61 }, { x: -39, z: 30 }] },
];

/** Broad, angular buttresses are part of the island solid, not rocks stamped over a smooth shell. */
function fracturedOutline(outline: Terrace["outline"], seed: number, span = 12, base = 0): Terrace["outline"] {
  const vertices: { x: number; z: number }[] = [];
  for (let i = 0; i < outline.length; i++) {
    const a = outline[i]!, b = outline[(i + 1) % outline.length]!;
    const dx = b.x - a.x, dz = b.z - a.z, length = Math.hypot(dx, dz);
    const count = Math.max(1, Math.round(length / span)), nx = dz / length, nz = -dx / length;
    vertices.push(a);
    for (let j = 0; j < count; j++) {
      const extension = base + 1.1 + hash2(i, j, seed) * 2.5;
      const recess = 0.35 + hash2(j, i, seed + 1) * 1.15 - base;
      for (const [fraction, offset] of [[0.18, -recess], [0.38, extension], [0.78, extension * 0.8], [0.94, -recess]] as const) {
        const t = (j + fraction) / count;
        vertices.push({ x: a.x + dx * t + nx * offset, z: a.z + dz * t + nz * offset });
      }
    }
  }
  return vertices;
}
export const SKULL_TERRACES: readonly Terrace[] = terraceFootprints.map((terrace, index) => ({
  ...terrace, outline: fracturedOutline(terrace.outline, 1801 + index * 31),
}));

// The wider, independently fractured beds break tall ribs into staggered interlocking
// sandstone masses. They are integrated terrain, including their broad embedded feet.
const cliffBeds: readonly Terrace[] = terraceFootprints.map((terrace, index) => ({
  id: `${terrace.id}-bed`, top: [4.8, 13.8, 26.8][index]!,
  outline: fracturedOutline(terrace.outline, 2303 + index * 43, 19, 3.1),
}));

/** Bare mineral caps between the broad foot blocks and planted terrace crests. */
export function skullCliffRockWeight(localX: number, localZ: number): number {
  for (let i = SKULL_TERRACES.length - 1; i >= 0; i--) {
    const crest = polygonDistance(localX, localZ, SKULL_TERRACES[i]!.outline);
    if (crest > 1.4) return 0;
    if (crest < -12) continue;
    const foot = polygonDistance(localX, localZ, cliffBeds[i]!.outline);
    if (foot > -2.2) return blend((-crest + 0.1) / 1.5) * blend((foot + 2.2) / 1.7);
  }
  return 0;
}

/** Planar foot, steep wall and chipped lip: the terrain itself carries the bevels. */
function cliffProfile(distance: number): number {
  const t = Math.max(0, Math.min(1, (distance + 2.2) / 3.6));
  if (t < 0.18) return t * 0.72;
  if (t < 0.78) return 0.1296 + (t - 0.18) * 1.3;
  return 0.9096 + (t - 0.78) * (0.0904 / 0.22);
}

export const SKULL_SITES: readonly { id: string; title: string; description: string; x: number; y: number; z: number; radius: number }[] = [
  { id: "landing", title: "Salvager's Landing", description: "A pointed sandy landing, boat-repair timbers and a clear approach to the old salvage gate.", ...point(-36, 1.8, -98), radius: 12 },
  { id: "lower-ward", title: "Salvage Gate and Lower Ward", description: "An open timber gate protects the cargo court. The western switchback climbs toward the cliff crossing.", ...point(-30, 8, -62), radius: 18 },
  { id: "cliff-crossing", title: "Cliff Crossing", description: "A high timber bridge spans the sheltered return path twelve metres below.", ...point(-25, 20, -8), radius: 16 },
  { id: "upper-fort", title: "Upper Salvage Fort", description: "A broken gun battery, canvas shelters and a climbed bastion crown the highest terrace.", ...point(8, 34, 24), radius: 23 },
  { id: "signal-mast", title: "Signal Mast Yard", description: "A salvaged ship's mast carries the fort's ragged signals high above the sea.", ...point(8, 34, 31), radius: 12 },
  { id: "offshore-watch", title: "Offshore Watch", description: "A raised watch on splayed stilts is reached by the eastern boardwalk and guarded stair.", ...point(78, 14, -52), radius: 10 },
  { id: "sheltered-stores", title: "Underbridge Stores", description: "Open-front stores shelter the low return path. It crosses beneath the bridge before rejoining the western ascent.", ...point(-25, 8, 5), radius: 12 },
];

type Path = { id: string; kind: "ground" | "bridge"; width: number; points: readonly SkullFeet[] };
export const SKULL_PATHS: readonly Path[] = [
  { id: "landing-gate", kind: "ground", width: 7, points: [point(-36, 1.8, -98), point(-36, 4, -86), point(-30, 8, -74), point(-30, 8, -62)] },
  { id: "western-ascent", kind: "ground", width: 5, points: [point(-30, 8, -62), point(-35, 8, -52), point(-52, 10, -52), point(-62, 15, -30), point(-52, 20, -8), point(-40, 20, -8)] },
  { id: "cliff-bridge", kind: "bridge", width: 4.5, points: [point(-40, 20, -8), point(-10, 20, -8)] },
  { id: "upper-ascent", kind: "ground", width: 5, points: [point(-10, 20, -8), point(-6, 20, -8), point(-6, 20, -24), point(14, 22, -24), point(38, 26, -10), point(42, 30, 12), point(26, 34, 22), point(8, 34, 24)] },
  { id: "underbridge-return", kind: "ground", width: 5, points: [point(-30, 8, -62), point(-12, 8, -42), point(-25, 8, -22), point(-25, 8, 8), point(-46, 12, 18), point(-66, 16, 12), point(-62, 20, -8), point(-52, 20, -8)] },
  { id: "watch-approach", kind: "ground", width: 5, points: [point(-30, 8, -62), point(5, 8, -62), point(26, 8, -52)] },
  { id: "watch-boardwalk", kind: "bridge", width: 3.5, points: [point(26, 8, -52), point(60, 8, -52)] },
  { id: "mast-yard", kind: "ground", width: 5, points: [point(8, 34, 24), point(8, 34, 31), point(14, 34, 31)] },
  { id: "lower-dig", kind: "ground", width: 4, points: [point(-30, 8, -62), point(-20, 8, -62)] },
  { id: "stores-dig", kind: "ground", width: 4, points: [point(-25, 8, 8), point(-25, 8, 5)] },
  { id: "supply-approach", kind: "ground", width: 4, points: [point(-36, 1.8, -98), point(-42, 1.8, -98)] },
  { id: "lower-watch-approach", kind: "ground", width: 3.5, points: [point(-30, 8, -62), point(-38, 8, -68), point(-59, 8, -68), point(-59, 8, -62)] },
  { id: "bastion-approach", kind: "ground", width: 4, points: [point(8, 34, 24), point(-2, 34, 24), point(-2, 34, 43), point(13, 34, 43)] },
  { id: "beach-sea-approach", kind: "ground", width: 14, points: [point(-36, .55, -118), point(-36, 1.1, -109), point(-36, 1.8, -98)] },
];

const shoreline = [
  { x: -44, z: -118 }, { x: -29, z: -118 }, { x: -22, z: -104 }, { x: 12, z: -87 }, { x: 39, z: -51 },
  { x: 61, z: -16 }, { x: 61, z: 34 }, { x: 41, z: 67 }, { x: 16, z: 81 },
  { x: -23, z: 72 }, { x: -48, z: 42 }, { x: -70, z: 15 }, { x: -73, z: -33 }, { x: -62, z: -77 },
] as const;

/** Ray intersection with the authored shore, shared by map outlines and the shore atlas. */
export function skullCoastRadius(theta: number): number {
  const ux = Math.cos(theta), uz = Math.sin(theta);
  let radius = Infinity;
  for (let i = 0, j = shoreline.length - 1; i < shoreline.length; j = i++) {
    const a = shoreline[j]!, b = shoreline[i]!;
    const dx = b.x - a.x, dz = b.z - a.z, denominator = ux * dz - uz * dx;
    if (Math.abs(denominator) < 1e-8) continue;
    const distance = (a.x * dz - a.z * dx) / denominator;
    const fraction = (a.x * uz - a.z * ux) / denominator;
    if (distance >= 0 && fraction >= 0 && fraction <= 1) radius = Math.min(radius, distance);
  }
  return radius;
}

/** Positive inside, negative outside; no temporary vectors or query-time allocation. */
function polygonDistance(x: number, z: number, outline: readonly { x: number; z: number }[]): number {
  let inside = false, distanceSquared = Infinity;
  for (let i = 0, j = outline.length - 1; i < outline.length; j = i++) {
    const a = outline[j]!, b = outline[i]!;
    if ((a.z > z) !== (b.z > z) && x < (b.x - a.x) * (z - a.z) / (b.z - a.z) + a.x) inside = !inside;
    const dx = b.x - a.x, dz = b.z - a.z;
    const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / (dx * dx + dz * dz)));
    const ex = x - a.x - dx * t, ez = z - a.z - dz * t;
    distanceSquared = Math.min(distanceSquared, ex * ex + ez * ez);
  }
  return Math.sqrt(distanceSquared) * (inside ? 1 : -1);
}
function blend(t: number): number { t = Math.max(0, Math.min(1, t)); return t * t * (3 - 2 * t); }
const pads = [
  { x0: -45, x1: -27, z0: -103, z1: -95, y: 1.8 },
  { x0: -38, x1: -15, z0: -72, z1: -54, y: 8 },
  { x0: -5, x1: 23, z0: 19, z1: 48, y: 34 },
  { x0: -35, x1: -20, z0: -2, z1: 10, y: 8 },
  { x0: -60, x1: -39, z0: -64, z1: -60, y: 8 },
  { x0: 12, x1: 37, z0: 38, z1: 48, y: 34 },
] as const;

export function skullTerrainHeight(localX: number, localZ: number, _baseHeight: number): number {
  const coast = polygonDistance(localX, localZ, shoreline);
  let h = coast >= 0 ? .4 + 1.4 * blend(coast / 5)
    : coast > -10 ? .4 - 3.4 * blend(-coast / 10)
    : -3 + (WORLD.seaFloor + 3) * blend((-coast - 10) / 36);
  for (let i = 0; i < SKULL_TERRACES.length; i++) {
    const terrace = SKULL_TERRACES[i]!, bed = cliffBeds[i]!;
    const footDistance = polygonDistance(localX, localZ, bed.outline);
    if (footDistance > -2.2 && coast > 0) {
      const phase = localX * 0.043 + localZ * 0.031 + i * 0.27;
      const facet = Math.abs(phase - Math.floor(phase) - 0.5);
      const bedTop = bed.top + (facet - 0.25) * (i === 0 ? 1.4 : 3.2);
      h += Math.max(0, bedTop - h) * cliffProfile(footDistance) * blend(coast / 2.5);
    }
    const distance = polygonDistance(localX, localZ, terrace.outline);
    if (distance > -2.2) h += Math.max(0, terrace.top - h) * cliffProfile(distance);
  }
  // Keep authored wall feet planted when a fractured crest recesses beneath their envelope.
  // Route grading below still has final authority over every entrance and carrying corridor.
  for (const foot of wallFootprints) {
    const distance = Math.hypot(Math.max(foot.x0 - localX, 0, localX - foot.x1),
      Math.max(foot.z0 - localZ, 0, localZ - foot.z1));
    if (distance < 0.8) h += Math.max(0, foot.y - h) * (1 - blend(distance / 0.8));
  }
  for (const pad of pads) {
    const d = Math.hypot(Math.max(pad.x0 - localX, 0, localX - pad.x1), Math.max(pad.z0 - localZ, 0, localZ - pad.z1));
    if (d < 2) h = pad.y + (h - pad.y) * blend(d / 2);
  }
  let nearest = Infinity, edgeDistance = Infinity, target = h;
  for (const route of SKULL_PATHS) {
    if (route.kind !== "ground") continue;
    for (let i = 1; i < route.points.length; i++) {
      const a = route.points[i - 1]!, b = route.points[i]!;
      const ax = a.x - SKULL_ORIGIN.x, az = a.z - SKULL_ORIGIN.z;
      const dx = b.x - a.x, dz = b.z - a.z;
      const t = Math.max(0, Math.min(1, ((localX - ax) * dx + (localZ - az) * dz) / (dx * dx + dz * dz)));
      const d = Math.hypot(localX - ax - dx * t, localZ - az - dz * t);
      // Select by centreline, not width: a wide route's end cap must not override a connected ascent.
      if (d < nearest) { nearest = d; edgeDistance = d - route.width / 2; target = a.y + (b.y - a.y) * t; }
    }
  }
  // Shoulder grading avoids near-vertical needles beside the route cut while leaving its width flat.
  if (edgeDistance < 4) h = target + (h - target) * blend(Math.max(0, edgeDistance) / 4);
  // Grade the suspended crossing's excavation instead of a discontinuous rectangular cut.
  const crossingEdge = Math.hypot(Math.max(-36 - localX, 0, localX + 14), Math.max(-11 - localZ, 0, localZ + 5));
  if (crossingEdge < 4) h -= Math.max(0, h - 8) * (1 - blend(crossingEdge / 4));
  const abutmentEdge = Math.hypot(Math.max(-40 - localX, 0, localX + 10), Math.max(-10.25 - localZ, 0, localZ + 5.75));
  if (abutmentEdge < 1) h -= Math.max(0, h - 19.68) * (1 - blend(abutmentEdge));
  // A floor must replace the ground below it, not occupy the same rendered plane.
  const storeEdge = Math.hypot(Math.max(-34 - localX, 0, localX + 29), Math.max(-1 - localZ, 0, localZ - 9));
  if (storeEdge < 0.6) h -= Math.max(0, h - 7.72) * (1 - blend(storeEdge / 0.6));
  return h;
}

type Solid = { id: string; site: string; material: "wood" | "plank" | "rock" | "rope" | "iron"; collider: Collider };
const solids: Solid[] = [];
function box(id: string, site: string, material: Solid["material"], x0: number, x1: number, z0: number, z1: number, y0: number, y1: number): void {
  solids.push({ id, site, material, collider: { kind: "box", minX: x0 + SKULL_ORIGIN.x, maxX: x1 + SKULL_ORIGIN.x, minZ: z0 + SKULL_ORIGIN.z, maxZ: z1 + SKULL_ORIGIN.z, y0, y1, blocksShip: false } });
}
function post(id: string, site: string, x: number, z: number, y0: number, y1: number, r = .24, blocksShip = false): void {
  solids.push({ id, site, material: "wood", collider: { kind: "cyl", x: x + SKULL_ORIGIN.x, z: z + SKULL_ORIGIN.z, r, y0, y1, blocksShip } });
}

// Split strips leave actual entrances and route cuts rather than invisible solid gates.
box("lower-palisade-south-west", "lower-ward", "wood", -48, -34, -74.4, -74, 8, 12);
box("lower-palisade-south-east", "lower-ward", "wood", -26, -9, -74.4, -74, 8, 12);
box("lower-gate-lintel", "lower-ward", "wood", -34, -26, -74.4, -74, 12, 12.6);
box("lower-palisade-east", "lower-ward", "wood", -9, -8.6, -74, -65, 8, 11.5);
box("lower-palisade-east-north", "lower-ward", "wood", -9, -8.6, -59, -49, 8, 11.5);
box("lower-palisade-north-west", "lower-ward", "wood", -28, -22, -49.4, -49, 8, 12);
box("lower-palisade-north-east", "lower-ward", "wood", -14, -9, -49.4, -49, 8, 12);
box("lower-palisade-west-south", "lower-ward", "wood", -49, -48.6, -74, -71, 8, 12);
box("lower-palisade-west-north", "lower-ward", "wood", -49, -48.6, -58, -55, 8, 12);
box("middle-palisade-east", "cliff-crossing", "wood", 49, 49.4, 4, 20, 20, 24);
box("middle-palisade-south", "cliff-crossing", "wood", 4, 14, -32.4, -32, 20, 24);
box("middle-palisade-west", "cliff-crossing", "wood", -44.4, -44, -27, -15, 20, 24);
box("upper-palisade-south", "upper-fort", "wood", -5, 20, 16.6, 17, 34, 38.3);
box("upper-palisade-gate-east", "upper-fort", "wood", 38, 43, 16.6, 17, 34, 38.3);
box("upper-gate-lintel", "upper-fort", "wood", 20, 38, 16.6, 17, 38.2, 38.8);
box("upper-palisade-west", "upper-fort", "wood", -5.4, -5, 17, 49, 34, 38);
box("upper-palisade-north-west", "upper-fort", "wood", -5, 12, 49, 49.4, 34, 38.4);
box("upper-palisade-north-east", "upper-fort", "wood", 19, 37, 49, 49.4, 34, 38.4);
box("upper-palisade-east", "upper-fort", "wood", 37, 37.4, 27, 38, 34, 37.5);
box("upper-palisade-fore-west", "upper-fort", "wood", -15, 2, 5, 5.4, 34, 35.6);
box("upper-palisade-fore-east", "upper-fort", "wood", 12, 30, 5, 5.4, 34, 35.4);
box("upper-palisade-fore-return", "upper-fort", "wood", -15.4, -15, 5, 20, 34, 36);
post("signal-mast", "signal-mast", 8, 36, 33.5, 64, .65);
box("mast-rigging-anchor-west", "signal-mast", "wood", -.6, .6, 38, 39.2, 34, 35);
box("mast-rigging-anchor-east", "signal-mast", "wood", 17.4, 18.6, 38, 39.2, 34, 35);

// Open-front stores keep the playable lane at x=-25 free all the way beneath the crossing.
box("stores-floor", "sheltered-stores", "plank", -34, -29, -1, 9, 7.8, 8);
box("stores-back-wall", "sheltered-stores", "wood", -34.4, -34, -1, 9, 8, 11.8);
box("stores-south-wall", "sheltered-stores", "wood", -34, -29, -1.4, -1, 8, 11.8);
box("stores-north-wall", "sheltered-stores", "wood", -34, -29, 9, 9.4, 8, 11.8);
box("stores-roof", "sheltered-stores", "plank", -34.4, -28.6, -1.4, 9.4, 11.8, 12.1);
box("stores-crate", "sheltered-stores", "wood", -33.5, -31.8, 4, 6, 8, 9.3);

for (const path of SKULL_PATHS) {
  if (path.kind !== "bridge") continue;
  const a = path.points[0]!, b = path.points[1]!;
  const x0 = a.x - SKULL_ORIGIN.x, x1 = b.x - SKULL_ORIGIN.x, z = a.z - SKULL_ORIGIN.z;
  const site = path.id === "cliff-bridge" ? "cliff-crossing" : "offshore-watch";
  // Seat the crossing into its abutments so both endpoint capsules stand on timber,
  // including approach tolerances, rather than dropping into the recessed terrain.
  const seat = path.id === "cliff-bridge" ? 0.5 : 0;
  box(`${path.id}-deck`, site, "plank", x0 - seat, x1 + seat, z - path.width / 2, z + path.width / 2, a.y - .3, a.y);
  for (const side of [-1, 1]) {
    const rz = z + side * (path.width / 2 + .06);
    box(`${path.id}-rail-${side}`, site, "rope", x0, x1, rz - .07, rz + .07, a.y, a.y + 1.15);
    for (let x = x0; x <= x1; x += 6) post(`${path.id}-post-${side}-${x}`, site, x, rz, a.y - .4, a.y + 1.3, .13);
  }
  for (const x of [x0 + 1, x1 - 1]) for (const side of [-1, 1]) {
    post(`${path.id}-support-${x}-${side}`, site, x, z + side * (path.width / 2 - .25), path.id === "cliff-bridge" ? 6 : -9, a.y - .3, .32, path.id === "watch-boardwalk");
  }
  if (path.id === "watch-boardwalk") {
    for (const x of [38, 49]) for (const side of [-1, 1]) post(`${path.id}-sea-pile-${x}-${side}`, site, x, z + side * (path.width / 2 - .25), -12, a.y - .3, .3, true);
  }
}

export const SKULL_TOWERS: readonly { id: string; x: number; y: number; z: number; width: number; depth: number; deck: number; height: number }[] = [
  { id: "gate-watch", ...point(-43, 8, -62), width: 8, depth: 8, deck: 13, height: 11 },
  { id: "upper-bastion", ...point(31, 34, 43), width: 10, depth: 10, deck: 40, height: 13 },
  { id: "offshore-watch", ...point(78, -9, -52), width: 10, depth: 10, deck: 14, height: 30 },
];
const towerWalks: { id: string; points: SkullFeet[] }[] = [];
for (const tower of SKULL_TOWERS) {
  const x = tower.x - SKULL_ORIGIN.x, z = tower.z - SKULL_ORIGIN.z;
  const x0 = x - tower.width / 2, x1 = x + tower.width / 2, z0 = z - tower.depth / 2, z1 = z + tower.depth / 2;
  const site = tower.id === "gate-watch" ? "lower-ward" : tower.id === "upper-bastion" ? "upper-fort" : "offshore-watch";
  box(`${tower.id}-deck`, site, "plank", x0, x1, z0, z1, tower.deck - .3, tower.deck);
  for (const px of [x0 + .3, x1 - .3]) for (const pz of [z0 + .3, z1 - .3]) {
    // Splayed legs own the lower offshore support; the upright continues only above their joint.
    post(`${tower.id}-stilt-${px}-${pz}`, site, px, pz, tower.id === "offshore-watch" ? tower.deck - .3 : tower.y, tower.y + tower.height, .3, tower.id === "offshore-watch");
  }
  if (tower.id === "offshore-watch") {
    for (const cornerX of [-1, 1]) for (const cornerZ of [-1, 1]) {
      const span = tower.deck - .3 - tower.y, segments = Math.ceil(span / 2);
      for (let i = 0; i < segments; i++) {
        const t = (i + .5) / segments, offset = 7.4 + (4.7 - 7.4) * t;
        post(`offshore-watch-splayed-${cornerX}-${cornerZ}-${i}`, site, x + cornerX * offset, z + cornerZ * offset, tower.y + span * i / segments, tower.y + span * (i + 1) / segments, .4, true);
      }
    }
  }
  box(`${tower.id}-rail-north`, site, "wood", x0, x1, z1 - .15, z1, tower.deck, tower.deck + 1.2);
  box(`${tower.id}-rail-south`, site, "wood", x0, x1, z0, z0 + .15, tower.deck, tower.deck + 1.2);
  box(`${tower.id}-rail-east`, site, "wood", x1 - .15, x1, z0, z1, tower.deck, tower.deck + 1.2);
  box(`${tower.id}-rail-west-north`, site, "wood", x0, x0 + .15, z + 1.65, z1, tower.deck, tower.deck + 1.2);
  box(`${tower.id}-rail-west-south`, site, "wood", x0, x0 + .15, z0, z - 1.65, tower.deck, tower.deck + 1.2);
  const startY = tower.id === "offshore-watch" ? 8 : tower.y;
  const steps = Math.round((tower.deck - startY) / .25), startX = x0 - steps * .5 - 1;
  const walk = [point(startX, startY, z)];
  box(`${tower.id}-stair-landing`, site, "plank", startX - .5, startX + 1, z - 1.5, z + 1.5, startY - .3, startY);
  for (let i = 0; i < steps; i++) {
    const sx = startX + 1 + i * .5, top = startY + (i + 1) * .25;
    box(`${tower.id}-step-${i}`, site, "plank", sx, sx + .5, z - 1.5, z + 1.5, top - .25, top);
    for (const side of [-1, 1]) box(`${tower.id}-stair-rail-${i}-${side}`, site, "rope", sx, sx + .5, z + side * 1.65 - .07, z + side * 1.65 + .07, top, top + 1.15);
    // Centres past the riser leave capsule overlap with the next tread below the torso.
    walk.push(point(sx + .25, top, z));
  }
  walk.push(point(x0, tower.deck, z), point(x, tower.deck, z));
  towerWalks.push({ id: tower.id, points: walk });
}
export const SKULL_SOLIDS: readonly Solid[] = solids;
const wallFootprints = solids.flatMap(solid => {
  const c = solid.collider;
  return c.kind === "box" && /palisade|wall/.test(solid.id) ? [{
    x0: c.minX - SKULL_ORIGIN.x, x1: c.maxX - SKULL_ORIGIN.x,
    z0: c.minZ - SKULL_ORIGIN.z, z1: c.maxZ - SKULL_ORIGIN.z, y: c.y0,
  }] : [];
});

export const SKULL_GARDENS = [
  [-35, -73, 12, 10], [-41, -29, 12, 12], [18, -33, 10, 10], [-19, 20, 16, 16],
  [27, 3, 10, 12], [-12, 53, 14, 15], [27, 62, 12, 10], [44, 27, 8, 8],
] as const;

export type SkullPalm = {
  x: number; z: number; height: number; seed: number; bend: number;
  trunk: readonly { a: SkullFeet; b: SkullFeet; radius: number; topRadius: number }[];
};

/** Decide retention once, before either renderer or collision sees a large trunk. */
function palmClear(x: number, z: number, margin: number): boolean {
  const wx = SKULL_ORIGIN.x + x, wz = SKULL_ORIGIN.z + z;
  for (const route of SKULL_PATHS) for (let i = 1; i < route.points.length; i++) {
    const a = route.points[i - 1]!, b = route.points[i]!, dx = b.x - a.x, dz = b.z - a.z;
    const t = Math.max(0, Math.min(1, ((wx - a.x) * dx + (wz - a.z) * dz) / (dx * dx + dz * dz)));
    if ((wx - a.x - dx * t) ** 2 + (wz - a.z - dz * t) ** 2 < (route.width / 2 + 1 + margin) ** 2) return false;
  }
  for (const dig of SKULL_ISLE.digSpots)
    if ((x - dig.x) ** 2 + (z - dig.z) ** 2 < (3 + margin) ** 2) return false;
  for (const { collider: c } of SKULL_SOLIDS) {
    const pad = 0.5 + margin;
    if (c.kind === "cyl" ? (wx - c.x) ** 2 + (wz - c.z) ** 2 < (c.r + pad) ** 2
      : wx > c.minX - pad && wx < c.maxX + pad && wz > c.minZ - pad && wz < c.maxZ + pad) return false;
  }
  return true;
}

function retainedPalms(): SkullPalm[] {
  const palms: SkullPalm[] = [];
  const retain = (x: number, z: number, height: number): void => {
    if (palms.some(p => (p.x - x) ** 2 + (p.z - z) ** 2 < 1.5 ** 2)) return;
    const seed = palms.length, bend = hash2(seed, 7, 441) * Math.PI * 2;
    const y = skullTerrainHeight(x, z, WORLD.seaFloor);
    const trunk: SkullPalm["trunk"][number][] = [];
    let a = point(x, y, z);
    for (let i = 1; i <= 7; i++) {
      const t = i / 7, b = point(x + Math.cos(bend) * t * t * height * 0.2, y + height * t, z + Math.sin(bend) * t * t * height * 0.2);
      trunk.push({ a, b, radius: 0.24 * (1 - (i - 1) / 7 * 0.4), topRadius: 0.24 * (1 - t * 0.4) });
      a = b;
    }
    palms.push({ x, z, height, seed, bend, trunk });
  };
  for (const p of authoredPalms) if (palmClear(p.x, p.z, 0.3))
    retain(p.x, p.z, 6.5 + hash2(palms.length, 2, 821) * 5.5);
  for (const [cx, cz, radius, count] of SKULL_GARDENS) for (let i = 0; i < count; i += 3) {
    const angle = i * 2.399 + cx, r = radius * Math.sqrt(hash2(i, cx, 921));
    const x = cx + Math.cos(angle) * r, z = cz + Math.sin(angle) * r;
    const y = skullTerrainHeight(x, z, WORLD.seaFloor);
    const slope = Math.abs(skullTerrainHeight(x + 0.7, z, WORLD.seaFloor) - y) + Math.abs(skullTerrainHeight(x, z + 0.7, WORLD.seaFloor) - y);
    if (y >= 3 && slope <= 1.1 && palmClear(x, z, 0.7)) retain(x, z, 6 + hash2(i, cx, 932) * 6);
  }
  return palms;
}

export const SKULL_PALMS: readonly SkullPalm[] = retainedPalms();
/** Conservative short cylinders enclose the same curved segments used by the renderer. */
export const SKULL_PALM_COLLIDERS: readonly Collider[] = SKULL_PALMS.flatMap(p => p.trunk.map(({ a, b, radius }) => ({
  kind: "cyl" as const, x: (a.x + b.x) / 2, z: (a.z + b.z) / 2,
  r: radius + Math.hypot(b.x - a.x, b.z - a.z) / 2,
  y0: a.y, y1: b.y, blocksShip: false,
})));

const path = (id: string) => SKULL_PATHS.find((p) => p.id === id)!.points;
const ascent = [...path("landing-gate"), ...path("western-ascent").slice(1), ...path("cliff-bridge").slice(1), ...path("upper-ascent").slice(1)];
const watch = [...path("landing-gate"), ...path("watch-approach").slice(1), ...path("watch-boardwalk").slice(1), ...towerWalks[2]!.points.slice(1)];
const bastion = [...ascent, ...path("bastion-approach").slice(1), ...towerWalks[1]!.points.slice(1)];
const gateWatch = [...path("landing-gate"), ...path("lower-watch-approach").slice(1), ...towerWalks[0]!.points];
const sideReturn = [...ascent, ...path("upper-ascent").slice(0, -1).reverse(), ...path("cliff-bridge").slice(0, -1).reverse(), point(-52, 20, -8), ...path("underbridge-return").slice(0, -1).reverse(), ...path("landing-gate").slice(0, -1).reverse()];
const digs = [
  [...path("landing-gate"), ...path("lower-dig").slice(1)],
  [...ascent, ...path("mast-yard").slice(1)],
  [...path("landing-gate"), ...path("underbridge-return").slice(1, 4), ...path("stores-dig").slice(1)],
];
export const SKULL_WALK_ROUTES: readonly { id: string; points: readonly SkullFeet[] }[] = [
  { id: "landing-upper-fort", points: ascent },
  { id: "fort-side-return", points: sideReturn },
  { id: "landing-signal-mast", points: [...ascent, ...path("mast-yard").slice(1, 2)] },
  { id: "landing-offshore-watch", points: watch },
  { id: "offshore-carry-return", points: [...watch].reverse() },
  { id: "landing-gate-watch", points: gateWatch },
  { id: "gate-watch-carry-return", points: [...gateWatch].reverse() },
  { id: "landing-upper-bastion", points: bastion },
  { id: "bastion-carry-return", points: [...bastion].reverse() },
  ...digs.flatMap((points, i) => [{ id: `skull-${i + 1}-dig`, points }, { id: `skull-${i + 1}-carry-extraction`, points: [...points].reverse() }]),
  { id: "landing-sheltered-stores", points: [...digs[2]!, point(-30.5, 8, 5)] },
  { id: "landing-supply-barrel6", points: path("supply-approach") },
  { id: "landing-sea-approach", points: path("beach-sea-approach") },
];
